import { ReactNode } from 'react'
import Tooltip from './Tooltip'
import { SIGNALS_GLOSSARY } from '../config/glossary'

interface Rule {
  pattern: RegExp
  explain: (match: RegExpExecArray) => string
}

// The phrases the backend's leverage rationale writes (leverage_calculator.py).
// Keep in step with its label formats; an unmatched phrase simply renders as
// plain text.
const RULES: Rule[] = [
  {
    pattern: /\b(\w+) regime \((\d+%) confidence\)/,
    explain: (m) => SIGNALS_GLOSSARY.regimeWithConfidence(m[1], m[2]),
  },
  {
    pattern: /\bregime confidence \d+%(?: \(low — reducing\))?/,
    explain: () => SIGNALS_GLOSSARY.regimeConfidence,
  },
  {
    pattern: /\b(?:low|moderate|elevated) volatility \((\d*\.\d+)\)/,
    explain: (m) => SIGNALS_GLOSSARY.volatility(Number(m[1])),
  },
  {
    pattern: /\b\d+× ceiling/,
    explain: () => SIGNALS_GLOSSARY.leverageCeiling,
  },
  {
    pattern: /\bR:R (\d+(?:\.\d+)?):1(?: \((weak|strong)\))?/,
    explain: (m) => SIGNALS_GLOSSARY.rewardRisk(Number(m[1]), m[2]),
  },
  {
    pattern: /\bNo liquidation risk \(no leverage applied\)/,
    explain: () => SIGNALS_GLOSSARY.noLiquidationRisk,
  },
]

// leverage_calculator.py's reason for a blocked setup, which no longer quotes EV.
const BLOCKED = "Leverage blocked: the setup did not clear the model's leverage gate."

/**
 * A stored rationale without its EV figure. Until Phase 2 has a trained model
 * the win probability behind EV is the Phase 1 score × 0.9, not a measured
 * probability, so the figure is not an expectation. Signals created before
 * the backend stopped writing it quote "EV +0.76", "EV +0.15 (caution zone,
 * capped 2×)" or, when blocked, "Leverage blocked: negative EV — …" followed
 * by sentences about negative EV; a blocked one becomes the current reason.
 */
export const stripEvClause = (text: string): string => {
  if (/^Leverage blocked:/.test(text) && /\bEV\b/.test(text)) return BLOCKED
  return text.replace(/\bEV [+-]\d+(?:\.\d+)?(?: \(caution zone, capped 2×\))?,\s*/g, '')
}

/** Splits `text` into plain runs and tooltip-wrapped jargon phrases. */
export const annotateRationale = (text: string): ReactNode[] => {
  const parts: ReactNode[] = []
  let rest = text
  let key = 0

  while (rest) {
    let best: { rule: Rule; match: RegExpExecArray } | null = null
    for (const rule of RULES) {
      const match = rule.pattern.exec(rest)
      if (match && (!best || match.index < best.match.index)) best = { rule, match }
    }

    if (!best) {
      parts.push(rest)
      break
    }

    const { rule, match } = best
    if (match.index > 0) parts.push(rest.slice(0, match.index))
    parts.push(
      <Tooltip key={key++} content={rule.explain(match)}>
        {match[0]}
      </Tooltip>
    )
    rest = rest.slice(match.index + match[0].length)
  }

  return parts
}

/** A model rationale sentence with its jargon explained inline. */
const RationaleText = ({ text }: { text: string }) => <>{annotateRationale(text)}</>

export default RationaleText
